import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Mail, Lock, ArrowRight, AlertCircle, Droplets, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { WHATSAPP_MANKLAR_URL, WEB_MANKLAR_URL } from '../lib/contacto';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GOOGLE_ERROR_MESSAGES = {
  NO_ACCOUNT: 'No existe ninguna cuenta Manklar asociada a ese correo de Google.',
  CUENTA_SUSPENDIDA: 'Tu cuenta está suspendida. Contacta con Manklar para más información.',
  CUENTA_DESACTIVADA: 'Tu cuenta está desactivada. Contacta con Manklar para más información.',
  GOOGLE_SESSION_INVALIDA: 'La sesión de Google ha caducado. Vuelve a intentarlo.',
  GOOGLE_ERROR: 'No se pudo completar el acceso con Google. Vuelve a intentarlo.',
};

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const googleError = location.state?.googleError;
  const googleErrorMsg = googleError ? (GOOGLE_ERROR_MESSAGES[googleError] || GOOGLE_ERROR_MESSAGES.GOOGLE_ERROR) : null;

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg(r.data.message || 'Solicitud enviada.');
    } catch {
      setForgotMsg('No se pudo procesar la solicitud. Inténtalo de nuevo.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = await login(email, password);
      // Role-based redirect
      const roleRedirects = {
        'admin': '/admin',
        'comercial': '/comercial',
        'tecnico': '/tecnico',
        'cliente': '/dashboard',
        'user': '/dashboard'
      };
      navigate(roleRedirects[userData.role] || '/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: '#030303' }}>
      {/* Premium background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-sky-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-emerald-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Glass line at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo Section - Premium presentation */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full scale-150" />
              <img
                src="/manklar-logo-white.png"
                alt="Manklar"
                className="h-16 sm:h-20 relative z-10 drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]"
              />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-sm">
            <Droplets className="w-4 h-4 text-sky-400" />
            <span className="text-xs sm:text-sm text-zinc-300 font-['Manrope']">Área Privada de Clientes</span>
          </div>
        </div>

        {/* Form Card - Glassmorphism style */}
        <div className="p-8 sm:p-10 rounded-2xl relative overflow-hidden" style={{ backgroundColor: 'rgba(10,10,10,0.8)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-white/5 rounded-full blur-3xl" />

          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl font-light text-white mb-2 font-['Outfit'] text-center">
              Bienvenido
            </h2>
            <p className="text-sm text-zinc-500 font-['Manrope'] mb-8 text-center">
              Accede a tu portal Manklar
            </p>

            {googleErrorMsg && (
              <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 backdrop-blur-sm" data-testid="google-error-banner">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-300">{googleErrorMsg}</p>
                    {googleError === 'NO_ACCOUNT' && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <a href={WHATSAPP_MANKLAR_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-xs text-white hover:bg-white/20 transition-colors" data-testid="google-error-prueba-link">
                          <Droplets className="w-3.5 h-3.5" />Solicitar prueba gratuita
                        </a>
                        <a href="mailto:info@manklar.com" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-zinc-300 hover:bg-white/10 transition-colors" data-testid="google-error-contacto-link">
                          <MessageCircle className="w-3.5 h-3.5" />Contactar con Manklar
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 rounded-xl flex items-center gap-3 bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full h-12 flex items-center justify-center gap-3 rounded-xl bg-white/[0.04] border border-white/15 text-white text-sm font-medium hover:bg-white/[0.08] hover:border-white/25 transition-all mb-6"
              data-testid="google-login-btn"
            >
              <GoogleIcon />
              Continuar con Google
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-['Manrope']">o con tu contraseña</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs tracking-wider uppercase text-zinc-500 mb-2 font-['Manrope']">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-600" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-12 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20 focus:bg-white/[0.05] rounded-xl transition-all"
                    placeholder="tu@email.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-zinc-500 mb-2 font-['Manrope']">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-600" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 h-12 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20 focus:bg-white/[0.05] rounded-xl transition-all"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-white text-black hover:bg-zinc-100 rounded-xl font-semibold transition-all shadow-[0_0_30px_rgba(255,255,255,0.15)] hover:shadow-[0_0_40px_rgba(255,255,255,0.25)] hover:scale-[1.02]"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    Iniciando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Acceder
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button type="button" onClick={() => { setShowForgot(!showForgot); setForgotMsg(''); }}
                className="text-xs text-zinc-500 hover:text-white transition-colors" data-testid="forgot-password-link">
                ¿Has olvidado tu contraseña?
              </button>
            </div>

            {showForgot && (
              <div className="mt-4 p-4 rounded-xl bg-white/[0.03] border border-white/10" data-testid="forgot-password-box">
                {forgotMsg ? (
                  <p className="text-xs text-emerald-400 text-center" data-testid="forgot-password-msg">{forgotMsg}</p>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-3">
                    <p className="text-xs text-zinc-400">Introduce tu correo y te enviaremos un enlace seguro (caduca en 1 hora) para crear una nueva contraseña.</p>
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="tu@email.com" required
                      className="h-10 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 rounded-xl"
                      data-testid="forgot-email-input" />
                    <Button type="submit" disabled={forgotLoading} variant="outline"
                      className="w-full h-10 border-white/20 text-white rounded-xl" data-testid="forgot-submit-btn">
                      {forgotLoading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                    </Button>
                  </form>
                )}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-white/5 text-center">
              <p className="text-sm text-zinc-500 font-['Manrope']">
                ¿No tienes cuenta?{' '}
                <a href={WHATSAPP_MANKLAR_URL} target="_blank" rel="noopener noreferrer" className="text-white hover:text-zinc-300 transition-colors" data-testid="solicitar-acceso-link">
                  Solicita una prueba gratuita
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Footer link */}
        <div className="mt-8 text-center">
          <a href={WEB_MANKLAR_URL} className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors font-['Manrope']">
            ← Volver a manklar.com
          </a>
        </div>
      </div>
    </div>
  );
}
